import React from 'react';

import { Text } from '@/components/shared/element/text';
import { HeadingLevel3 } from '@/components/shared/headline/heading-level-3';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateCommonImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const ImageWrap = styled(Box, {
  opacity: '0.15',
});

const IconWrap = styled('div', {
  position: 'relative',
  mx: 'auto',
  variants: {
    size: {
      1: {
        size: 102,
      },
      2: {
        size: 72,
        '@sm': {
          size: 90,
        },
      },
    },
  },
  defaultVariants: {
    size: '1',
  },
});

const IconCircle = styled(Box, {
  variants: {
    variant: {
      gray: {
        bc: '$gray300',
      },
      grayDark: {
        bc: '$gray600',
      },
      blue: {
        bc: '$gray600',
      },
      white: {
        bc: '$white',
      },
      green: {
        bc: '$white',
      },
    },
  },
  defaultVariants: {
    variant: 'gray',
  },
});

const IconDecorate = styled(Box, {
  '&:nth-child(1)': {
    size: 26,
  },
  '&:nth-child(2)': {
    size: 16,
  },
  '&:nth-child(3)': {
    size: 8,
  },
});

const IconDecorateWrap = styled(Box, {
  variants: {
    variant: {
      gray: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            bc: '$primary',
            opacity: '0.3',
          },
          '&:nth-child(3)': {
            bc: '$white',
            opacity: '0.4',
          },
        },
      },
      red: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            bc: '$black',
            opacity: '0.2',
          },
          '&:nth-child(3)': {
            bc: '$white',
            opacity: '0.4',
          },
        },
      },
      blue: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            opacity: '0.5',
            bc: '$blue',
          },
          '&:nth-child(3)': {
            bc: '$gray600',
          },
        },
      },
      white: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            opacity: '0.5',
            bc: '$primary',
          },
          '&:nth-child(3)': {
            bc: '$gray500',
          },
        },
      },
      green: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            opacity: '0.5',
            bc: '$green-lightest',
          },
          '&:nth-child(3)': {
            bc: '$gray500',
          },
        },
      },
      'blue-medium': {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            opacity: '0.5',
            bc: '$blue-medium',
          },
          '&:nth-child(3)': {
            bc: '$primary-100',
          },
        },
      },
      redGray: {
        [`& ${IconDecorate}`]: {
          '&:nth-child(1), &:nth-child(2)': {
            opacity: '0.5',
            bc: '$primary',
          },
          '&:nth-child(3)': {
            bc: '$gray500',
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'gray',
  },
});

const Item = styled(Box);

const Items = styled(Flex, {
  variants: {
    size: {
      1: {
        flexDirection: 'column',
        justifyContent: 'center',
        columnGap: '$space-16',
        rowGap: '$space-8',
        '@lg': {
          flexDirection: 'row',
        },
        [`& ${Item}`]: {
          '@lg': {
            flex: '0 0 25%',
            maxWidth: 260,
          },
        },
      },
      2: {
        columnGap: '$space-6',
        rowGap: '$space-8',
        justifyContent: 'space-evenly',
        '@sm': {
          columnGap: '$space-16',
          justifyContent: 'center',
        },
        '@lg': {
          columnGap: '$space-20',
        },
        [`& ${Item}`]: {
          flex: '0 0 calc(50% - 12px)',
          maxWidth: 180,
          '@sm': {
            flex: '0 0 160px',
            maxWidth: 160,
          },
        },
      },
    },
    decorate: {
      true: {
        [`& ${Item}`]: {
          '&:nth-child(4n+1)': {
            [`& ${IconDecorate}`]: {
              '&:nth-child(1)': {
                bottom: 0,
                left: -4,
              },
              '&:nth-child(2)': {
                top: 23,
                right: -6,
              },
              '&:nth-child(3)': {
                top: 44,
                left: 'calc(100% + 6px)',
              },
            },
          },
          '&:nth-child(4n+2)': {
            [`& ${IconDecorate}`]: {
              '&:nth-child(1)': {
                top: 0,
                left: -4,
              },
              '&:nth-child(2)': {
                bottom: 23,
                right: -6,
              },
              '&:nth-child(3)': {
                bottom: 44,
                left: 'calc(100% + 6px)',
              },
            },
          },
          '&:nth-child(4n+3)': {
            [`& ${IconDecorate}`]: {
              '&:nth-child(1)': {
                bottom: 0,
                right: -4,
              },
              '&:nth-child(2)': {
                top: 23,
                left: -6,
              },
              '&:nth-child(3)': {
                top: 44,
                right: 'calc(100% + 6px)',
              },
            },
          },
          '&:nth-child(4n+4)': {
            [`& ${IconDecorate}`]: {
              '&:nth-child(1)': {
                top: 0,
                right: -4,
              },
              '&:nth-child(2)': {
                bottom: 23,
                left: -6,
              },
              '&:nth-child(3)': {
                bottom: 44,
                right: 'calc(100% + 6px)',
              },
            },
          },
        },
      },
    },
  },
});

const ItemText = styled(Text, {
  maxHeight: '999999px',
  minHeight: '1px',
});

export interface IServiceFeature {
  theme?:
    | 'gray'
    | 'dark'
    | 'red'
    | 'blue'
    | 'grayDark'
    | 'blueDark'
    | 'green'
    | 'grayLight';
  size?: 'normal' | 'small';
  decorate?: boolean;
  title?: string;
  description?: string;
  buttons?: IButton[];
  imageMobile?: ImageProps;
  imageDesktop?: ImageProps;
  fade?: boolean;
  items?: {
    icon?: ImageProps;
    title?: string;
    description?: string;
  }[];
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
}

const View: React.FC<IServiceFeature> = React.memo(
  ({
    theme = 'gray',
    decorate = true,
    size = 'normal',
    dataTestSection = 'section_service_feature',
    dataTest = 'service_feature',
    dataTrack = 'service-feature',
    ...props
  }) => {
    const bgVariant = () => {
      switch (theme) {
        case 'gray':
          return 'gradient-gray';
        case 'dark':
        case 'grayDark':
          return 'gradient-gray-dark';
        case 'red':
          return 'red-dark';
        case 'blue':
        case 'blueDark':
        case 'grayLight':
          return 'gradient-gray-light';
        case 'green':
          return 'gray-light';
        default:
          return 'gradient-gray';
      }
    };

    const titleVariant = () => {
      switch (theme) {
        case 'gray':
        case 'dark':
        case 'grayDark':
        case 'red':
          return 'text-white';
        case 'blue':
          return 'text-blue';
        case 'blueDark':
          return 'text-blue-dark';
        case 'green':
          return 'text-green';
        case 'grayLight':
          return 'text-gray-darkest';
        default:
          return 'text-white';
      }
    };

    const textVariant = () => {
      switch (theme) {
        case 'gray':
        case 'dark':
        case 'grayDark':
        case 'red':
          return 'text-white';
        case 'blue':
          return 'text-gray-dark';
        case 'blueDark':
          return 'text-blue-dark';
        case 'green':
          return 'text-black';
        case 'grayLight':
          return 'text-gray-darkest';
        default:
          return 'text-white';
      }
    };

    const iconVariant = () => {
      switch (theme) {
        case 'gray':
        case 'dark':
        case 'red':
          return 'gray';
        case 'blue':
          return 'blue';
        case 'grayDark':
          return 'grayDark';
        case 'blueDark':
          return 'white';
        case 'green':
          return 'green';
        case 'grayLight':
          return 'white';
        default:
          return 'gray';
      }
    };

    const iconCircleVariant = () => {
      switch (theme) {
        case 'gray':
        case 'dark':
        case 'grayDark':
          return 'gray';
        case 'red':
        case 'blueDark':
          return 'blue-medium';
        case 'green':
          return 'green';
        case 'blue':
          return 'blue';
        case 'grayLight':
          return 'redGray';
        default:
          return 'gray';
      }
    };

    return (
      <Section backgroundColor={bgVariant()} test={dataTestSection}>
        {!!props.imageMobile && !!props.imageDesktop && (
          <ImageWrap position="absolute" inset="0" pointerEvents="none">
            <Image
              rootMargin="20% 0px 0px 0px"
              priority={props.imageMobile.priority}
              src={props.imageMobile.src}
              alt={props.imageMobile.alt}
              width={props.imageMobile.width}
              height={props.imageMobile.height}
              layout="fill"
              sources={generateCommonImageSources({
                mobileSrc: props.imageMobile.src,
                mobileOptions: {
                  quality: 65,
                },
                desktopSrc: props.imageDesktop.src,
                desktopOptions: {
                  quality: 65,
                },
              })}
            />
          </ImageWrap>
        )}
        <Section
          as="div"
          size="2xl"
          test="feature"
          css={{
            '@lg': {
              minHeight: 450,
              display: 'grid',
              alignItems: 'center',
            },
          }}
        >
          <HeadingLevel3
            title={props.title}
            description={props.description}
            theme={theme || 'gray'}
            dataTrack={dataTrack}
            dataTest={`${dataTest}_button_cta`}
          />
          <Container>
            {!!props.items && props.items.length > 0 && (
              <Items
                wrap="wrap"
                decorate={decorate}
                size={size === 'small' ? '2' : '1'}
              >
                {props.items.map((o, i) => (
                  <Item key={i} data-test={`${dataTest}_item`}>
                    <Grid rowGap={size === 'small' ? '3' : '6'}>
                      {!!o.icon && (
                        <Box>
                          <IconWrap size={size === 'small' ? '2' : '1'}>
                            <IconCircle
                              position="absolute"
                              inset="0"
                              size="full"
                              rounded="full"
                              variant={iconVariant()}
                              data-test={`${dataTest}_item_icon`}
                            >
                              <Image
                                rootMargin="20% 0px 0px 0px"
                                priority={o.icon.priority}
                                src={o.icon.src}
                                alt={o.title as string}
                                width={90}
                                height={90}
                                layout="responsive"
                                aspectRatio="1by1"
                              />
                            </IconCircle>
                            {!!decorate && (
                              <IconDecorateWrap
                                position="absolute"
                                inset="0"
                                size="full"
                                variant={iconCircleVariant()}
                              >
                                <IconDecorate
                                  position="absolute"
                                  rounded="full"
                                  zIndex="1"
                                />
                                <IconDecorate
                                  position="absolute"
                                  rounded="full"
                                  zIndex="1"
                                />
                                <IconDecorate
                                  position="absolute"
                                  rounded="full"
                                  zIndex="1"
                                />
                              </IconDecorateWrap>
                            )}
                          </IconWrap>
                        </Box>
                      )}

                      {!!(o.title || o.description) && (
                        <Box textAlign="center">
                          <Grid rowGap="2">
                            {!!o.title && (
                              <ItemText
                                as="p"
                                size={size === 'small' ? 'text-base' : 'h5'}
                                font="bold"
                                wrap
                                variant={titleVariant()}
                                dangerouslySetInnerHTML={{ __html: o.title }}
                                data-test={`${dataTest}_item_title`}
                              />
                            )}
                            {!!o.description && (
                              <ItemText
                                as="p"
                                size={
                                  size === 'small' ? 'text-base' : 'text-body-1'
                                }
                                wrap
                                variant={textVariant()}
                                dangerouslySetInnerHTML={{
                                  __html: o.description,
                                }}
                                data-test={`${dataTest}_item_description`}
                              />
                            )}
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                  </Item>
                ))}
              </Items>
            )}
          </Container>
          <HeadingLevel3
            buttons={props.buttons}
            theme={theme || 'gray'}
            dataTrack={dataTrack}
            dataTest={`${dataTest}_button_cta`}
          />
        </Section>
      </Section>
    );
  }
);
View.displayName = 'SectionServiceFeature';
export default View;
